import React, {useCallback} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import * as S from './Filters.styles';

import RefineSearch from './RefineSearch';
import PriceRangeFilter from './PriceRange';
import PopularFilter from './Popular';
import StarRatinFilter from './StarRating';
import CustomerRatingFilter from './CustomerRating';
import HotelChainFilter from './HotelChain';
import AmenitiesFilter from './Amenities';
import HotelTypeFilter from './HotelType';

import Chip from '../../../../../stories/components/hotels/common/Chip/Chip';
import BackToTopCard from '../../../../../stories/components/hotels/SRP/BackToTopCard/BackToTopCard';
import FilterLoader from './../../../../../stories/components/hotels/Loadingpage/SrpFilters/SrpFilters';

import {
  IUpdate_Filter,
  UPDATE_FILTER,
  IFilters,
  RESET_FILTER,
  ICityMetaInfo,
} from '../../redux/types';

import {fetchSrpDataThunk, removeFilterThunk} from '../../redux/thunks';

import {
  hasDataSelectorFn,
  srpDataSelectorFn,
  srpConfigSelectorFn,
  hotelChainListSelectorFn,
} from '../../redux/selectors';

import {
  findFixedFilter,
  fixedFilters,
  updateFilterForSingleSelection,
  fixedPopularFilter,
} from '../../utils/filter-utils';
import {isObjectEmpty} from '../../../../utils/common-utils';
import scrollToTop from '../../../../../shared/utils/scrollToTop';

const Filters = ({cityData}) => {
  const dispatch = useDispatch();

  // Note for later: we can batch multiple selector lookups and return multiple instead
  const hasData = useSelector(hasDataSelectorFn);
  const {city_meta_info} = useSelector(srpDataSelectorFn);
  const {filter: appliedFilters} = useSelector(srpConfigSelectorFn);
  const hotelChainFilterList = useSelector(hotelChainListSelectorFn);

  const updateFiltersCaller = useCallback(
    (actionData: IUpdate_Filter['actionData']) => {
      scrollToTop();
      dispatch({type: UPDATE_FILTER, actionData});
      dispatch(fetchSrpDataThunk());
    },
    [dispatch],
  );

  const resetFiltersCaller = useCallback(() => {
    dispatch({type: RESET_FILTER});
    dispatch(fetchSrpDataThunk());
  }, [dispatch]);

  const removeFilterCaller = useCallback(
    (filterTarget, value) => {
      dispatch(removeFilterThunk(filterTarget, value));
      dispatch(fetchSrpDataThunk());
    },
    [dispatch],
  );

  let oapcityCus = '1';
  let custCursor = 'all';
  if (hasData === null)(
    oapcityCus = '0.5',
    custCursor = 'none'
  );

  let tabsList = [];
  let hotelTypeFiltersList = [];
  let amenitiesFiltersList = [];

  if (city_meta_info) {
    const {flt, qf, aty, am} = city_meta_info as ICityMetaInfo;
    tabsList = flt;
    hotelTypeFiltersList = aty;
    amenitiesFiltersList = am;
  }

  const {
    type: hotelTypeFilterListInState,
    chain: hotelChainFilterListInState,
    amenities: amenitiesFilterListInState,
    step_range: priceRangeFilterInState,
    range_ugc: customerRatingFilterInState,
    star: starRatingFilterInState,
  } = appliedFilters as IFilters;

  const listOfFilterTypesToSkipInAppliedFiltersDisplayList = ['pcf', 'lid', 'tag'];

  const renderChipList = () => {
    if (isObjectEmpty(appliedFilters)) return null;

    if (tabsList && tabsList.length > 0) {
      const tabsListItemIsPresent = tabsList.find(
        item => item.fd && Object.keys(item.fd).length > 0,
      );
      if (tabsListItemIsPresent)
        listOfFilterTypesToSkipInAppliedFiltersDisplayList.push(
          Object.keys(tabsListItemIsPresent.fd)[0],
        );
    }

    return Object.keys(appliedFilters)
      .filter(
        appliedFiltersKeyName =>
          listOfFilterTypesToSkipInAppliedFiltersDisplayList.indexOf(appliedFiltersKeyName) === -1,
      )
      .map(itemKey => {
        if (appliedFilters[itemKey]) {
          if (appliedFilters[itemKey].length > 0) {
            if (['step_range', 'star', 'range_ugc'].indexOf(itemKey) > -1) {
              if (itemKey === 'range_ugc') {
                return (
                  <Chip
                    key={itemKey + 'appliedFilterChipChild'}
                    text={
                      findFixedFilter({
                        src: fixedFilters[itemKey].data,
                        target: appliedFilters[itemKey],
                        type: itemKey,
                      }).name
                    }
                    actionHandler={() => {
                      removeFilterCaller(itemKey, appliedFilters[itemKey]);
                    }}
                  />
                );
              }

              return appliedFilters[itemKey].map((item, cindex) => {
                return (
                  <Chip
                    key={cindex + 'appliedFilterChipChild'}
                    text={
                      findFixedFilter({
                        src: fixedFilters[itemKey].data,
                        target: item,
                        type: itemKey,
                      }).name
                    }
                    actionHandler={() => {
                      removeFilterCaller(itemKey, item);
                    }}
                  />
                );
              });
            }

            // REST OF THE FILTERS HANDLING
            if (Array.isArray(appliedFilters[itemKey])) {
              return appliedFilters[itemKey].map((item, cindex) => {
                const isLocationSearch = item.id_type && item.id_type === 'voy';
                const isQuickFilter =
                  ['location', 'landmark', 'transit', 'location_search', 'utag'].indexOf(itemKey) >
                  -1;
                const isKnownHermesFilter = ['amenities', 'chain', 'type'].indexOf(itemKey) > -1;
                let text = item;
                let arg = {};
                if (isLocationSearch) {
                  text = item.n;
                  arg[itemKey] = appliedFilters[itemKey].filter(
                    appliedFilterItem =>
                      appliedFilterItem.loc_id && appliedFilterItem.loc_id !== item.loc_id,
                  );
                }

                if (isQuickFilter) {
                  text = item.n;
                  arg[itemKey] = appliedFilters[itemKey].filter(
                    appliedFilterItem =>
                      appliedFilterItem.loc_id && appliedFilterItem.loc_id !== item.loc_id,
                  );
                }

                if (isKnownHermesFilter) {
                  arg[itemKey] = appliedFilters[itemKey].filter(
                    appliedFilterItem => appliedFilterItem !== item,
                  );
                }

                return (
                  <Chip
                    key={cindex + 'appliedFilterChipChild'}
                    text={text}
                    actionHandler={() => {
                      updateFiltersCaller(arg);
                    }}
                  />
                );
              });
            }

            let arg = {};
            arg[itemKey] = '';
            if (itemKey === 'pc') {
              arg = {...arg, pcf: 0};
            }
            return (
              <Chip
                key={itemKey + 'appliedFilterChipChild'}
                text={appliedFilters[itemKey]}
                actionHandler={() => {
                  updateFiltersCaller(arg);
                }}
              />
            );
          } else if (['fc', 'pah', 'fbfi', 'ibf', 'gs'].indexOf(itemKey) > -1) {
            return (
              <Chip
                key={itemKey}
                text={fixedPopularFilter[itemKey]}
                actionHandler={() => {
                  const filterDictForItemUnderAction = {};
                  filterDictForItemUnderAction[itemKey] = appliedFilters[itemKey];
                  updateFiltersCaller(
                    updateFilterForSingleSelection(filterDictForItemUnderAction, appliedFilters),
                  );
                }}
              />
            );
          } 
        }
      });
  };

  const hasAppliedFilter =
    appliedFilters &&
    Object.keys(appliedFilters).length > 0 &&
    Object.keys(appliedFilters).find(itemKey => {
      if (listOfFilterTypesToSkipInAppliedFiltersDisplayList.indexOf(itemKey) > -1) return false;

      if (Array.isArray(appliedFilters[itemKey])) {
        return appliedFilters[itemKey].length > 0;
      }

      return !!appliedFilters[itemKey];
    });

  return (
    <>
      <S.FiltersWrapperDiv opacity={oapcityCus} pointer={custCursor}>
        {(hasAppliedFilter && Object.keys(appliedFilters).length && (
          <S.AppliedFilterWrapper>
            <S.FiltersHeaderBlockDiv>
              <S.FlexColumnDiv>
                <S.FilterHeading>APPLIED FILTERS</S.FilterHeading>
              </S.FlexColumnDiv>

              <S.FlexColumnDiv>
                <S.ClearTextSpan
                  isactive={hasAppliedFilter ? 'true' : 'false'}
                  onClick={() => {
                    resetFiltersCaller();
                  }}
                >
                  CLEAR ALL
                </S.ClearTextSpan>
              </S.FlexColumnDiv>
            </S.FiltersHeaderBlockDiv>
            <S.AppliedFiltersWrapperDiv>{renderChipList()}</S.AppliedFiltersWrapperDiv>
          </S.AppliedFilterWrapper>
        )) ||
          null}

        <S.FiltersInfoBlockWrapperDiv>
          <S.FiltersHeaderBlockDiv>
            <S.FlexColumnDiv>
              <S.FilterHeading>REFINE SEARCH</S.FilterHeading>
            </S.FlexColumnDiv>
            <RefineSearch />
          </S.FiltersHeaderBlockDiv>
        </S.FiltersInfoBlockWrapperDiv>

        <S.FilterCheckBoxListWrapper>

          <PopularFilter updateFilter={updateFiltersCaller} appliedFilters={appliedFilters} cityData={cityData}/>

          <PriceRangeFilter
            updateFilter={updateFiltersCaller}
            priceRangeFilter={priceRangeFilterInState}
          />

          <StarRatinFilter
            updateFilter={updateFiltersCaller}
            starRatingFilter={starRatingFilterInState}
          />

          <CustomerRatingFilter
            updateFilter={updateFiltersCaller}
            customerRatingFilter={customerRatingFilterInState}
          />

          {hotelChainFilterList && hotelChainFilterList.length > 0 && (
            <HotelChainFilter
              updateFilter={updateFiltersCaller}
              hotelChainFilter={hotelChainFilterListInState}
            />
          )}

          {amenitiesFiltersList && amenitiesFiltersList.length > 0 && (
            <AmenitiesFilter
              updateFilter={updateFiltersCaller}
              amenitiesFiltersList={amenitiesFiltersList}
              amenitiesFilter={amenitiesFilterListInState}
            />
          )}

          {hotelTypeFiltersList && hotelTypeFiltersList.length > 0 && (
            <HotelTypeFilter
              updateFilter={updateFiltersCaller}
              hotelTypeFiltersList={hotelTypeFiltersList}
              hotelTypeFilter={hotelTypeFilterListInState}
            />
          )}
        </S.FilterCheckBoxListWrapper>
      </S.FiltersWrapperDiv>
      <BackToTopCard />
    </>
  );
};

export default React.memo(Filters);
